import type { LessonDeliveryState } from '@classroom-ai/types';
import type { PeriodState } from 'src/layouts/header/header-model';

// ----------------------------------------------------------------------
// "Wrap up today" (epic #647) — which unmarked lessons the Dashboard card lists and the
// nav badge counts. The list itself is the server's (`lessonDelivery.unmarked`: the
// caller's own classes, from the year's marking start up to today, oldest first).

export type UnmarkedLesson = {
  classId: string;
  lessonPlanId: string;
  unitId: string | null;
  title: string;
  kind: string | null;
  date: string;
  today: boolean;
};

export type WrapUp = {
  /** Every period of today has ended (or nothing meets today). */
  dayOver: boolean;
  /** Earlier days, oldest first, then today's lessons once the day is over. */
  rows: UnmarkedLesson[];
  /** Lessons from earlier days still unmarked. */
  carried: number;
};

/** The teacher's day is over once every one of today's meetings has ended. */
export function isDayOver(states: Map<string, PeriodState>): boolean {
  return [...states.values()].every((s) => s.state === 'done');
}

export function wrapUp(lessons: UnmarkedLesson[], states: Map<string, PeriodState>): WrapUp {
  const dayOver = isDayOver(states);
  const earlier = lessons.filter((l) => !l.today);
  const today = dayOver ? lessons.filter((l) => l.today) : [];
  return { dayOver, rows: [...earlier, ...today], carried: earlier.length };
}

/** The Dashboard nav badge: earlier days' unmarked lessons, plus today's once the day is over. */
export const badgeCount = (w: WrapUp) => w.rows.length;

/** A week-grid cell's one-word mark for a past meeting; null when there's nothing to show. */
export function cellMark(state: LessonDeliveryState | null | undefined): { text: string; color: 'success' | 'warning' | 'error' | 'default' } | null {
  switch (state) {
    case 'TAUGHT':
      return { text: 'Taught', color: 'success' };
    case 'PARTLY_TAUGHT':
      return { text: 'Partly', color: 'warning' };
    case 'NOT_TAUGHT':
      return { text: 'Not taught', color: 'error' };
    case 'UNMARKED':
      return { text: 'Not marked', color: 'default' };
    default:
      return null;
  }
}
